/**
 * Animation Controller Hook
 * Manages animation state, timing, and progressive isochrone layer rendering.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import type {
  Coordinates,
  TravelMode,
  AnimationConfig,
} from '../types';
import { fetchIsochroneRange } from '../services/isochroneService';
import { canMakeCalculation } from '../services/calculationService';
import { renderIsochroneLayer, clearIsochroneLayers } from '../utils/mapLayers';
import type { Map as MapboxMap } from 'mapbox-gl';

/**
 * Animation state type
 */
type AnimationState = 'idle' | 'playing' | 'paused' | 'completed';

/**
 * Target frame rate for animation (60 FPS)
 */
const TARGET_FPS = 60;

/**
 * Default animation speed multiplier
 */
const DEFAULT_SPEED = 1;

/**
 * Returns from useAnimationController hook
 */
export interface UseAnimationControllerReturn {
  /** Current animation state */
  animationState: AnimationState;
  /** Current animation time (0 to maxTime) */
  currentTime: number;
  /** Whether data is being loaded */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Start or resume animation */
  startAnimation: (
    origin: Coordinates,
    maxTime: number,
    mode: TravelMode,
    speed?: number,
    userId?: string
  ) => Promise<void>;
  /** Pause animation */
  pauseAnimation: () => void;
  /** Resume paused animation */
  resumeAnimation: () => void;
  /** Restart animation from beginning */
  restartAnimation: () => void;
  /** Set animation speed */
  setSpeed: (speed: number) => void;
  /** Get current animation time */
  getCurrentTime: () => number;
  /** Set map instance for rendering */
  setMapInstance: (map: MapboxMap | null) => void;
}

/**
 * Animation Controller Hook
 * Manages animation state and progressive rendering.
 */
export function useAnimationController(): UseAnimationControllerReturn {
  const [animationState, setAnimationState] =
    useState<AnimationState>('idle');
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for animation control
  const animationFrameRef = useRef<number | null>(null);
  const configRef = useRef<AnimationConfig | null>(null);
  const isochronesRef = useRef<Map<number, GeoJSON.Polygon>>(new Map());
  const mapInstanceRef = useRef<MapboxMap | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);
  const lastRenderedTimeRef = useRef<number[] | null>(null); // Track last rendered times array to prevent unnecessary updates

  /**
   * Set map instance for rendering
   */
  const setMapInstance = useCallback((map: MapboxMap | null) => {
    mapInstanceRef.current = map;
  }, []);

  /**
   * Calculate time intervals for animation
   */
  const calculateTimeIntervals = useCallback(
    (maxTime: number, timeStep: number): number[] => {
      const intervals: number[] = [];
      for (let time = timeStep; time <= maxTime; time += timeStep) {
        intervals.push(time);
      }
      // Always include the max time
      if (intervals.length === 0 || intervals[intervals.length - 1] < maxTime) {
        intervals.push(maxTime);
      }
      return intervals;
    },
    []
  );

  /**
   * Cancel animation frame
   */
  const cancelAnimation = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  /**
   * Animation loop using requestAnimationFrame
   * Each minute of travel time = 1 second of real time (at 1x speed)
   * Speed multiplier: 1x = 1 min/sec, 0.5x = 0.5 min/sec (slower), 2x = 2 min/sec (faster)
   */
  const animate = useCallback(() => {
    if (!configRef.current || !mapInstanceRef.current) {
      return;
    }

    const config = configRef.current;
    const map = mapInstanceRef.current;
    const now = performance.now();
    const elapsed = (now - startTimeRef.current) / 1000; // Convert to seconds

    // Calculate current time: 1 second of real time = speed minutes of travel time
    // At 1x speed: 1 second = 1 minute of travel time
    const newCurrentTime = pausedTimeRef.current + elapsed * config.speed;

    if (newCurrentTime >= config.maxTime) {
      // Animation completed
      setCurrentTime(config.maxTime);
      setAnimationState('completed');
      cancelAnimation();

      // Render final isochrone
      const finalPolygon = isochronesRef.current.get(config.maxTime);
      if (finalPolygon) {
        renderIsochroneLayer(map, finalPolygon, config.maxTime);
        lastRenderedTimeRef.current = [config.maxTime];
      }
    } else {
      // Find the closest isochrone time (the current minute being displayed)
      // We always show only the current minute's isochrone for clarity
      // Speed controls how fast we progress through minutes, not how many to show
      const closestTime = Array.from(isochronesRef.current.keys())
        .filter((time) => time <= newCurrentTime)
        .sort((a, b) => b - a)[0]; // Get the highest time <= currentTime

      // Only render if the time has changed (prevents unnecessary updates and blinking)
      if (closestTime !== undefined && closestTime !== lastRenderedTimeRef.current?.[0]) {
        // Update current time to match the rendered isochrone time (for progress bar sync)
        setCurrentTime(closestTime);
        
        const polygon = isochronesRef.current.get(closestTime);
        if (polygon) {
          // Render only the current minute's isochrone
          renderIsochroneLayer(map, polygon, closestTime);
          lastRenderedTimeRef.current = [closestTime];
        }
      } else if (closestTime !== undefined) {
        // Still update current time to match rendered isochrone for progress bar
        setCurrentTime(closestTime);
      }

      // Continue animation
      animationFrameRef.current = requestAnimationFrame(animate);
    }
  }, [cancelAnimation]);

  /**
   * Start or resume animation
   */
  const startAnimation = useCallback(
    async (
      origin: Coordinates,
      maxTime: number,
      mode: TravelMode,
      speed: number = DEFAULT_SPEED,
      userId?: string
    ) => {
      try {
        setError(null);
        cancelAnimation();

        if (!mapInstanceRef.current) {
          throw new Error('Map instance not set');
        }

        // If paused, resume from current time
        if (animationState === 'paused') {
          setAnimationState('playing');
          startTimeRef.current = performance.now();
          animationFrameRef.current = requestAnimationFrame(animate);
          return;
        }

        // If idle or completed, start fresh
        setIsLoading(true);
        setAnimationState('idle');

        // Check usage limit if user is authenticated
        if (userId) {
          const canCalculate = await canMakeCalculation(userId);
          if (!canCalculate) {
            setIsLoading(false);
            setAnimationState('idle');
            setError('USAGE_LIMIT_EXCEEDED');
            return;
          }
        }

        // Calculate time step (1 minute per step for precise speed control)
        // This allows us to show multiple minutes per second at higher speeds
        const timeStep = 1;
        const frameRate = TARGET_FPS;
        const intervals = calculateTimeIntervals(maxTime, timeStep);

        // Pre-fetch all isochrones (pass userId for tracking)
        const isochrones = await fetchIsochroneRange(origin, intervals, mode, userId);
        isochronesRef.current = isochrones;

        // Notify usage counter to refresh (if user is authenticated)
        if (userId) {
          window.dispatchEvent(new CustomEvent('usageUpdated', { detail: { userId } }));
        }
        
        // Track isochrone calculation in PostHog
        (async () => {
          const { trackEvent } = await import('../services/posthogService');
          trackEvent('isochrone_calculation_started', {
            origin,
            max_time: maxTime,
            travel_mode: mode,
            speed,
            intervals_count: intervals.length,
            isochrones_fetched: isochrones.size,
          });
        })();

        // Store configuration
        configRef.current = {
          origin,
          maxTime,
          travelMode: mode,
          speed,
          frameRate,
          timeStep,
        };

        // Clear existing layers
        clearIsochroneLayers(mapInstanceRef.current);

        // Reset animation state
        pausedTimeRef.current = 0;
        setCurrentTime(0);
        lastRenderedTimeRef.current = null; // Reset last rendered time
        setIsLoading(false);
        setAnimationState('playing');

        // Start animation
        startTimeRef.current = performance.now();
        animationFrameRef.current = requestAnimationFrame(animate);
      } catch (err) {
        setIsLoading(false);
        setAnimationState('idle');
        
        // Handle usage limit exceeded error
        if (err instanceof Error && err.message === 'USAGE_LIMIT_EXCEEDED') {
          setError('USAGE_LIMIT_EXCEEDED');
        } else {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to start animation';
          setError(errorMessage);
        }
        console.error('Animation start error:', err);
        
        // Track error in PostHog
        (async () => {
          const { trackEvent } = await import('../services/posthogService');
          trackEvent('isochrone_calculation_failed', {
            origin,
            max_time: maxTime,
            travel_mode: mode,
            error_message: err instanceof Error ? err.message : 'Unknown error',
          });
        })();
      }
    },
    [animationState, animate, calculateTimeIntervals, cancelAnimation]
  );

  /**
   * Pause animation
   */
  const pauseAnimation = useCallback(() => {
    if (animationState === 'playing' && configRef.current) {
      // Calculate current time when pausing
      const now = performance.now();
      const elapsed = (now - startTimeRef.current) / 1000; // seconds
      const calculatedTime = pausedTimeRef.current + elapsed * configRef.current.speed;
      
      // Store the paused time
      pausedTimeRef.current = calculatedTime;
      
      // Cancel animation loop
      cancelAnimation();
      
      // Update state
      setAnimationState('paused');
    }
  }, [animationState, cancelAnimation]);

  /**
   * Resume animation
   */
  const resumeAnimation = useCallback(() => {
    if (animationState === 'paused') {
      setAnimationState('playing');
      startTimeRef.current = performance.now();
      animationFrameRef.current = requestAnimationFrame(animate);
    }
  }, [animationState, animate]);

  /**
   * Restart animation
   */
  const restartAnimation = useCallback(() => {
    cancelAnimation();
    if (configRef.current) {
      pausedTimeRef.current = 0;
      setCurrentTime(0);
      setAnimationState('playing');
      startTimeRef.current = performance.now();
      animationFrameRef.current = requestAnimationFrame(animate);
    }
  }, [animate, cancelAnimation]);

  /**
   * Set animation speed
   */
  const setSpeed = useCallback((speed: number) => {
    if (speed <= 0) {
      return;
    }
    if (configRef.current) {
      // If currently playing, calculate current time and reset timer to prevent jumps
      if (animationState === 'playing') {
        const now = performance.now();
        const elapsed = (now - startTimeRef.current) / 1000; // seconds
        const calculatedTime = pausedTimeRef.current + elapsed * configRef.current.speed;
        pausedTimeRef.current = calculatedTime;
        startTimeRef.current = now;
        
        // Clear last rendered time to force re-render with new speed
        lastRenderedTimeRef.current = null;
      } else if (animationState === 'paused') {
        pausedTimeRef.current = currentTime;
      }
      
      configRef.current.speed = speed;
    }
  }, [animationState, currentTime]);

  /**
   * Get current animation time
   */
  const getCurrentTime = useCallback(() => {
    return currentTime;
  }, [currentTime]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelAnimation();
    };
  }, [cancelAnimation]);

  return {
    animationState,
    currentTime,
    isLoading,
    error,
    startAnimation,
    pauseAnimation,
    resumeAnimation,
    restartAnimation,
    setSpeed,
    getCurrentTime,
    setMapInstance,
  };
}

